import { useMemo, useState } from 'react'
import { calculateTextStats, formatTime } from '@/lib/text-stats'
import { cn } from '@/lib/utils'
import { ChevronDown, ChevronUp } from 'lucide-react'

interface WritingStatsProps {
  content: string
  className?: string
}

export function WritingStats({ content, className }: WritingStatsProps) {
  const [expanded, setExpanded] = useState(false)

  const stats = useMemo(() => calculateTextStats(content), [content])

  if (!content || stats.words === 0) {
    return null
  }

  return (
    <div className={cn('flex-shrink-0 border-t border-border bg-muted/30', className)}>
      {/* Collapsed view - essential stats */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full px-4 py-2 flex items-center justify-between text-xs text-muted-foreground hover:text-foreground transition-colors"
      >
        <div className="flex items-center gap-4 flex-wrap">
          <span>{stats.words.toLocaleString()} words</span>
          <span className="text-border">|</span>
          <span>{stats.characters.toLocaleString()} chars</span>
          <span className="text-border">|</span>
          <span>{formatTime(stats.readingTime)} read</span>
        </div>
        {expanded ? (
          <ChevronUp className="w-4 h-4 flex-shrink-0" />
        ) : (
          <ChevronDown className="w-4 h-4 flex-shrink-0" />
        )}
      </button>

      {/* Expanded view - all stats */}
      {expanded && (
        <div className="px-4 pb-3 pt-1 border-t border-border/50">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-2 text-xs">
            {/* Core counts */}
            <div className="space-y-1">
              <h4 className="font-medium text-foreground mb-2">Counts</h4>
              <div className="flex justify-between text-muted-foreground">
                <span>Characters</span>
                <span className="tabular-nums">{stats.characters.toLocaleString()}</span>
              </div>
              <div className="flex justify-between text-muted-foreground">
                <span>Chars (no spaces)</span>
                <span className="tabular-nums">{stats.charactersNoSpaces.toLocaleString()}</span>
              </div>
              <div className="flex justify-between text-muted-foreground">
                <span>Words</span>
                <span className="tabular-nums">{stats.words.toLocaleString()}</span>
              </div>
              <div className="flex justify-between text-muted-foreground">
                <span>Sentences</span>
                <span className="tabular-nums">{stats.sentences.toLocaleString()}</span>
              </div>
              <div className="flex justify-between text-muted-foreground">
                <span>Paragraphs</span>
                <span className="tabular-nums">{stats.paragraphs.toLocaleString()}</span>
              </div>
              <div className="flex justify-between text-muted-foreground">
                <span>Pages</span>
                <span className="tabular-nums">{stats.pages}</span>
              </div>
            </div>

            {/* Readability */}
            <div className="space-y-1">
              <h4 className="font-medium text-foreground mb-2">Readability</h4>
              <div className="flex justify-between text-muted-foreground">
                <span>Avg word length</span>
                <span className="tabular-nums">{stats.avgWordLength}</span>
              </div>
              <div className="flex justify-between text-muted-foreground">
                <span>Avg sentence</span>
                <span className="tabular-nums">{stats.avgSentenceLength} words</span>
              </div>
              <div className="flex justify-between text-muted-foreground">
                <span>Avg paragraph</span>
                <span className="tabular-nums">{stats.avgWordsPerParagraph} words</span>
              </div>
              <div className="flex justify-between text-muted-foreground">
                <span>Syllables</span>
                <span className="tabular-nums">{stats.syllables.toLocaleString()}</span>
              </div>
            </div>

            {/* Time estimates */}
            <div className="space-y-1 col-span-2 sm:col-span-1">
              <h4 className="font-medium text-foreground mb-2">Time Estimates</h4>
              <div className="flex justify-between text-muted-foreground">
                <span>Reading time</span>
                <span className="tabular-nums">{formatTime(stats.readingTime)}</span>
              </div>
              <div className="flex justify-between text-muted-foreground">
                <span>Speaking time</span>
                <span className="tabular-nums">{formatTime(stats.speakingTime)}</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
